/**
 * Bandeau "Installer l'app RealEase" + tutoriel iOS.
 *
 * - Sur Android/Chrome/Edge : intercepte beforeinstallprompt et propose un bouton
 *   "Installer" qui declenche le prompt natif.
 * - Sur iOS Safari (qui ne supporte pas beforeinstallprompt) : affiche un tutoriel
 *   manuel "Partager > Sur l'ecran d'accueil".
 * - Si l'app est deja installee (mode standalone), ne s'affiche pas.
 * - Memorise le rejet pendant 14 jours via localStorage.
 */
import React, { useEffect, useState } from "react";

const DISMISS_KEY = "realease_pwa_dismissed_at";
const DISMISS_DAYS = 14;

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

const isSafari = () =>
  /^((?!chrome|android|crios|fxios).)*safari/i.test(navigator.userAgent);

const wasRecentlyDismissed = () => {
  try {
    const ts = localStorage.getItem(DISMISS_KEY);
    if (!ts) return false;
    const days = (Date.now() - parseInt(ts, 10)) / 86400000;
    return days < DISMISS_DAYS;
  } catch {
    return false;
  }
};

const InstallPWAPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (wasRecentlyDismissed()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS Safari : pas d'evenement, on affiche le bandeau apres 4s
    if (isIOS() && isSafari()) {
      const t = setTimeout(() => setShow(true), 4000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      };
    }

    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      setShow(false);
      if (outcome === "dismissed") {
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
      }
    } else if (isIOS() && isSafari()) {
      setShowIOSGuide(true);
    }
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setShow(false);
  };

  if (!show) return null;

  return (
    <>
      <div
        className="fixed bottom-0 left-0 right-0 md:bottom-4 md:left-4 md:right-auto md:max-w-md z-[60] animate-slide-up"
        data-testid="pwa-install-prompt"
      >
        <div className="m-3 md:m-0 bg-white rounded-2xl shadow-2xl border border-[#E8DCC4] p-4 flex items-center gap-3">
          <img
            src="/icon-192.png"
            alt="RealEase"
            className="w-12 h-12 rounded-xl flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <p className="font-bold text-gray-900 text-sm">Installer RealEase</p>
            <p className="text-xs text-gray-600 truncate">
              Acces rapide depuis votre ecran d'accueil
            </p>
          </div>
          <button
            onClick={dismiss}
            className="text-gray-400 hover:text-gray-600 px-2"
            data-testid="pwa-install-dismiss"
            aria-label="Plus tard"
          >
            ✕
          </button>
          <button
            onClick={handleInstall}
            className="px-4 py-2 bg-[#008A8D] text-white text-sm font-bold rounded-lg hover:bg-[#006B6E] flex-shrink-0"
            data-testid="pwa-install-btn"
          >
            Installer
          </button>
        </div>
      </div>

      {showIOSGuide && (
        <div
          className="fixed inset-0 bg-black/60 z-[70] flex items-end md:items-center justify-center p-4"
          onClick={() => setShowIOSGuide(false)}
          data-testid="pwa-ios-guide"
        >
          <div
            className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg text-gray-900">Installer sur iPhone</h3>
              <button
                onClick={() => setShowIOSGuide(false)}
                className="text-gray-400 hover:text-gray-600 text-xl"
                aria-label="Fermer"
              >
                &times;
              </button>
            </div>
            <ol className="space-y-3 text-sm text-gray-700">
              <li className="flex gap-3 items-start">
                <span className="bg-[#008A8D] text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                <span>
                  Appuyez sur le bouton <b>Partager</b>
                  <span className="inline-block ml-1 align-middle">
                    <svg className="w-5 h-5 inline text-[#008A8D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h8m-8 4h8m-8-8h8M4 6v12a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2z" />
                    </svg>
                  </span>
                  en bas de Safari.
                </span>
              </li>
              <li className="flex gap-3 items-start">
                <span className="bg-[#008A8D] text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                <span>Faites defiler et appuyez sur <b>"Sur l'ecran d'accueil"</b>.</span>
              </li>
              <li className="flex gap-3 items-start">
                <span className="bg-[#008A8D] text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                <span>Confirmez avec <b>Ajouter</b>. L'icone <b>RealEase</b> apparait sur votre ecran d'accueil !</span>
              </li>
            </ol>
            <div className="mt-5 p-3 bg-[#FDF8F0] rounded-lg text-xs text-[#A57F4F]">
              <b>💡 Astuce :</b> Une fois installee, RealEase s'ouvre comme une vraie app, en plein ecran sans la barre Safari.
            </div>
            <button
              onClick={() => { dismiss(); setShowIOSGuide(false); }}
              className="mt-4 w-full px-4 py-2.5 bg-[#008A8D] text-white text-sm font-bold rounded-lg hover:bg-[#006B6E]"
            >
              J'ai compris
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallPWAPrompt;
